import { useEffect, useRef } from "react";

interface AxisConfig {
  cursor: string;
  ariaOrientation: "horizontal" | "vertical";
  containerClass: string;
  gripClass: string;
  coordinate: (e: { clientX: number; clientY: number }) => number;
}

// axis "y" = drag up/down (row height, handle on the bottom edge);
// axis "x" = drag left/right (column width, handle on the right edge).
const AXIS: Record<"x" | "y", AxisConfig> = {
  y: {
    cursor: "ns-resize",
    ariaOrientation: "horizontal",
    containerClass: "inset-x-0 bottom-0 h-2.5 cursor-ns-resize",
    gripClass: "h-1 w-8",
    coordinate: (e) => e.clientY,
  },
  x: {
    cursor: "ew-resize",
    ariaOrientation: "vertical",
    containerClass: "inset-y-0 right-0 w-2.5 cursor-ew-resize",
    gripClass: "h-8 w-1",
    coordinate: (e) => e.clientX,
  },
};

interface ResizeHandleProps {
  axis: "x" | "y";
  value: number;
  clamp: (value: number) => number;
  onChange: (value: number) => void;
  onReset?: () => void;
  ariaLabel: string;
  title: string;
}

/**
 * Drag handle on a schedule row/column edge to adjust its size. Emits the
 * clamped new size continuously while the pointer is held down (live
 * preview); double-click resets to the default size. Uses Pointer Events so
 * it works with mouse, touch, and pen — important because the read-only
 * timeline is used on the (often mobile) client-share page.
 */
export function ResizeHandle({
  axis,
  value,
  clamp,
  onChange,
  onReset,
  ariaLabel,
  title,
}: ResizeHandleProps) {
  const config = AXIS[axis];

  // Latest props for the document-level listeners so a drag in progress never
  // reads stale values, and a cleanup ref so an unmount mid-drag can tear the
  // listeners down (they'd otherwise linger until the next pointerup and keep
  // firing onChange for a row that no longer exists).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const clampRef = useRef(clamp);
  clampRef.current = clamp;
  const teardownRef = useRef<(() => void) | null>(null);

  useEffect(() => () => teardownRef.current?.(), []);

  const handlePointerDown = (e: React.PointerEvent) => {
    // Only start on the primary (left) button; a right/middle-click would
    // otherwise open the context menu, swallow the pointerup, and leave the
    // page stuck in resize state.
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const start = config.coordinate(e);
    const startValue = value;

    const handlePointerMove = (ev: PointerEvent) => {
      onChangeRef.current(clampRef.current(startValue + (config.coordinate(ev) - start)));
    };
    const teardown = () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", teardown);
      document.removeEventListener("pointercancel", teardown);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      teardownRef.current = null;
    };
    teardownRef.current = teardown;

    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", teardown);
    document.addEventListener("pointercancel", teardown);
    document.body.style.cursor = config.cursor;
    document.body.style.userSelect = "none";
  };

  return (
    <div
      role="separator"
      aria-orientation={config.ariaOrientation}
      aria-label={ariaLabel}
      title={title}
      // touch-none stops the browser from scrolling/panning while dragging.
      className={`group/resizehandle absolute z-40 flex touch-none items-center justify-center ${config.containerClass}`}
      onPointerDown={handlePointerDown}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset?.();
      }}
    >
      <div
        // Faintly visible at rest (so it's discoverable on touch, where there
        // is no hover), brighter on hover.
        className={`rounded-full bg-muted-foreground/20 transition-opacity group-hover/resizehandle:bg-muted-foreground/50 ${config.gripClass}`}
      />
    </div>
  );
}
